import { STAGE } from '../stage.js';
/**
 * What the media worker (and the kit) measured of a package's files (W4). `meshFacts` is pure and is
 * copied, from `OVERHEAD` on, into services/media-worker/src/world/mesh-facts.ts (copies.test.ts there holds it
 * equal) and, whole, into the kit (tools/wereld-kit `sync`, held by its copies.test.ts).
 */
/** Scenery from here up hangs over a sight line, not across it (the sight profile, ADR-053). */
export const OVERHEAD = 4.4;
/**
 * The disc the ground's cover masks are measured over: ADR-032's apron, kept when ADR-061 shrank the
 * floor so the masks stored before stay as long and readable; the checker asks only the points within
 * `STAGE.floor` to be covered.
 */
export const COVER_RADIUS = 8.5;
/** Sample points over the stage disc, every 25 cm (inclusive of the rim): the ground must cover them all. */
export const STAGE_POINTS = (() => {
    const step = 0.25;
    const n = Math.round(COVER_RADIUS / step);
    const points = [];
    for (let i = -n; i <= n; i++) {
        for (let j = -n; j <= n; j++) {
            if (Math.hypot(i * step, j * step) <= COVER_RADIUS + 1e-9)
                points.push([i * step, j * step]);
        }
    }
    return points;
})();
/** The length of a full cover mask, in hex digits. */
export const COVER_DIGITS = Math.ceil(STAGE_POINTS.length / 4);
/** The nearest a 2D triangle comes to the origin (0 when it covers it). */
function nearest(ax, az, bx, bz, cx, cz) {
    const sign = (px, pz, qx, qz, rx, rz) => (px - rx) * (qz - rz) - (qx - rx) * (pz - rz);
    const d1 = sign(0, 0, ax, az, bx, bz);
    const d2 = sign(0, 0, bx, bz, cx, cz);
    const d3 = sign(0, 0, cx, cz, ax, az);
    // A triangle seen edge-on from above (a wall in line with the centre) has no inside: its edges say.
    const flat = Math.abs(sign(ax, az, bx, bz, cx, cz)) < 1e-12;
    if (!flat && !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0)))
        return 0;
    const edge = (px, pz, qx, qz) => {
        const ex = qx - px;
        const ez = qz - pz;
        const t = Math.min(1, Math.max(0, -(px * ex + pz * ez) / Math.max(1e-12, ex * ex + ez * ez)));
        return Math.hypot(px + ex * t, pz + ez * t);
    };
    return Math.min(edge(ax, az, bx, bz), edge(bx, bz, cx, cz), edge(cx, cz, ax, az));
}
/** Marks the STAGE_POINTS inside a triangle (x, z) in `bits`. */
function cover(bits, ax, az, bx, bz, cx, cz) {
    const area = (bx - ax) * (cz - az) - (cx - ax) * (bz - az);
    if (Math.abs(area) < 1e-12)
        return;
    const step = 0.25;
    const n = Math.round(COVER_RADIUS / step);
    const lo = (v) => Math.max(-n, Math.floor(v / step));
    const hi = (v) => Math.min(n, Math.ceil(v / step));
    const i0 = lo(Math.min(ax, bx, cx));
    const i1 = hi(Math.max(ax, bx, cx));
    const j0 = lo(Math.min(az, bz, cz));
    const j1 = hi(Math.max(az, bz, cz));
    for (let i = i0; i <= i1; i++) {
        for (let j = j0; j <= j1; j++) {
            const x = i * step;
            const z = j * step;
            if (Math.hypot(x, z) > COVER_RADIUS + 1e-9)
                continue;
            const w0 = ((bx - x) * (cz - z) - (cx - x) * (bz - z)) / area;
            const w1 = ((cx - x) * (az - z) - (ax - x) * (cz - z)) / area;
            const w2 = 1 - w0 - w1;
            const slack = 1e-9;
            if (w0 >= -slack && w1 >= -slack && w2 >= -slack) {
                const at = pointIndex(i, j, n);
                bits[at >> 3] |= 1 << (at & 7);
            }
        }
    }
}
/** The index in STAGE_POINTS of grid cell (i, j): points are listed i-major, j inside, rim-clipped. */
const INDEX_OF = (() => {
    const step = 0.25;
    const n = Math.round(COVER_RADIUS / step);
    const table = new Map();
    let k = 0;
    for (let i = -n; i <= n; i++) {
        for (let j = -n; j <= n; j++) {
            if (Math.hypot(i * step, j * step) <= COVER_RADIUS + 1e-9)
                table.set((i + n) * (2 * n + 1) + (j + n), k++);
        }
    }
    return table;
})();
function pointIndex(i, j, n) {
    return INDEX_OF.get((i + n) * (2 * n + 1) + (j + n));
}
const hex = (bits) => {
    let out = '';
    // Nibble k holds points 4k..4k+3.
    for (let k = 0; k < COVER_DIGITS; k++) {
        let nibble = 0;
        for (let b = 0; b < 4; b++) {
            const at = k * 4 + b;
            if (at < STAGE_POINTS.length && (bits[at >> 3] & (1 << (at & 7))) !== 0)
                nibble |= 1 << b;
        }
        out += nibble.toString(16);
    }
    return out;
};
/** OR of cover masks; undefined when one is not a mask of the right length. */
export function unionCover(masks) {
    const covered = new Array(STAGE_POINTS.length).fill(false);
    for (const mask of masks) {
        if (mask.length !== COVER_DIGITS || !/^[0-9a-f]+$/.test(mask))
            return undefined;
        for (let k = 0; k < COVER_DIGITS; k++) {
            const nibble = parseInt(mask[k], 16);
            for (let b = 0; b < 4; b++)
                if (nibble & (1 << b))
                    covered[k * 4 + b] = true;
        }
    }
    return covered;
}
/**
 * One mesh's facts from its positions in world space (x, y, z per vertex) and its index, if any.
 * Throws on a position that is not a finite number or an index outside the positions: a mesh like
 * that is undecodable (the worker answers 422), never judged.
 */
export function meshFacts(name, positions, index) {
    for (let v = 0; v < positions.length; v++) {
        if (!Number.isFinite(positions[v]))
            throw new Error(`mesh "${name}" has a position that is not a number`);
    }
    const vertices = Math.floor(positions.length / 3);
    const count = index ? index.length : vertices;
    const vertex = (k) => {
        const at = index ? index[k] : k;
        if (!Number.isInteger(at) || at < 0 || at >= vertices)
            throw new Error(`mesh "${name}" has an index outside its positions`);
        return at * 3;
    };
    const ground = name.startsWith('grond');
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (let v = 0; v + 2 < positions.length; v += 3) {
        for (let k = 0; k < 3; k++) {
            min[k] = Math.min(min[k], positions[v + k]);
            max[k] = Math.max(max[k], positions[v + k]);
        }
    }
    const bits = new Uint8Array(Math.ceil(STAGE_POINTS.length / 8));
    let flatOnStage = true;
    let onApron = false;
    for (let t = 0; t + 2 < count; t += 3) {
        const a = vertex(t);
        const b = vertex(t + 1);
        const c = vertex(t + 2);
        const ys = [positions[a + 1], positions[b + 1], positions[c + 1]];
        const low = Math.min(...ys);
        const high = Math.max(...ys);
        const close = nearest(positions[a], positions[a + 2], positions[b], positions[b + 2], positions[c], positions[c + 2]);
        if (close <= STAGE.floor && Math.max(Math.abs(low), Math.abs(high)) > STAGE.below + 1e-6)
            flatOnStage = false;
        if (high > STAGE.apronHeight && low < STAGE.clearHeight && close < STAGE.clear - 1e-6)
            onApron = true;
        if (ground &&
            close <= COVER_RADIUS &&
            Math.max(Math.abs(low), Math.abs(high)) <= STAGE.below + 1e-6)
            cover(bits, positions[a], positions[a + 2], positions[b], positions[b + 2], positions[c], positions[c + 2]);
    }
    return {
        name,
        triangles: Math.floor(count / 3),
        ground,
        flatOnStage,
        onApron,
        cover: ground ? hex(bits) : '',
        bounds: vertices > 0 ? { min, max } : { min: [0, 0, 0], max: [0, 0, 0] },
    };
}
// --- sight lines (generator v6, ADR-053) --------------------------------------
/**
 * What hides a model from the stage (ADR-053): seen from a seated pupil's eyes at the stage centre,
 * for every 5° of azimuth and every ring, the highest elevation, in whole degrees rounded up, of any
 * scenery nearer than that ring. A point of a model at distance d is hidden when it lies below the
 * cell of the last ring at or before d. Azimuth 0 is straight ahead (−z), positive to the right (+x);
 * bin 0 starts at −180°.
 */
export const SIGHT = {
    /** Degrees per azimuth bin. */
    bin: 5,
    bins: 72,
    /** The eyes: a seated pupil, 1.2 m above the stage centre. */
    eye: 1.2,
    /** Ring distances, metres, ascending: a cell holds what is nearer than its ring. */
    rings: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 20, 22, 25, 30],
    /** A cell with nothing in it. */
    none: -90,
    /** At most this far apart, metres, are the points sampled on a surface. */
    step: 0.5,
    /** Nearer than this is the clear cylinder (ADR-061), never scenery. */
    from: 4.5,
};
/** A profile with nothing in it, to add to. */
export function emptySight() {
    return Array.from({ length: SIGHT.bins }, () => SIGHT.rings.map(() => SIGHT.none));
}
/** The bin of a ground-plan point: its azimuth from straight ahead (−z), right positive. */
export function sightBin(x, z) {
    const azimuth = (Math.atan2(x, -z) * 180) / Math.PI;
    return Math.min(SIGHT.bins - 1, Math.max(0, Math.floor((azimuth + 180) / SIGHT.bin)));
}
/**
 * One point of scenery: every ring beyond it sees it at its elevation from the eyes. Nothing from
 * OVERHEAD up counts: a ceiling, a canopy or the sea's surface hangs over a sight line, not across it.
 */
export function addSightPoint(profile, x, y, z) {
    const r = Math.hypot(x, z);
    if (y >= OVERHEAD || r < SIGHT.from || r >= SIGHT.rings[SIGHT.rings.length - 1])
        return;
    const elevation = Math.ceil((Math.atan2(y - SIGHT.eye, r) * 180) / Math.PI);
    const row = profile[sightBin(x, z)];
    for (let k = 0; k < SIGHT.rings.length; k++) {
        if (SIGHT.rings[k] > r && row[k] < elevation)
            row[k] = elevation;
    }
}
/**
 * How many points one GLB's triangles may be sampled at (the worker's event loop is synchronous: a
 * file of huge triangles must not hold it for minutes). Past it the step widens, so a file only gets
 * a coarser profile, never a stuck worker.
 */
export const SIGHT_BUDGET = 50_000_000;
/** A fresh budget, one per GLB. */
export function sightBudget() {
    return { left: SIGHT_BUDGET, scale: 1 };
}
/** The longest edge sampled whole; a longer triangle is first cut to the square the last ring fits in. */
const WHOLE_EDGE = SIGHT.step * 200;
/** `polygon` cut to one side of a plane `axis` (0 = x, 2 = z) at `limit`, keeping `sign * (v - limit) <= 0`. */
function clipSide(polygon, axis, limit, sign) {
    const out = [];
    const inside = (p) => sign * (p[axis] - limit) <= 0;
    for (let i = 0; i < polygon.length; i++) {
        const p = polygon[i];
        const q = polygon[(i + 1) % polygon.length];
        if (inside(p))
            out.push(p);
        if (inside(p) !== inside(q)) {
            const t = (limit - p[axis]) / (q[axis] - p[axis]);
            out.push([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t, p[2] + (q[2] - p[2]) * t]);
        }
    }
    return out;
}
/**
 * Every triangle of a mesh in world space (x, y, z per vertex; `index` or none) as points at most
 * `SIGHT.step` apart (200 to an edge at most), skipping what lies wholly beyond the last ring: a
 * triangle with an edge over 100 m is cut in four until its pieces are short, so a huge triangle costs
 * what the part in range costs. `budget` (one per GLB) widens the step when a file asks for too much.
 */
export function addTriangleSight(profile, positions, index, budget = sightBudget()) {
    const count = index ? index.length : Math.floor(positions.length / 3);
    const at = (k) => (index ? index[k] : k) * 3;
    for (let t = 0; t + 2 < count; t += 3) {
        const a = at(t);
        const b = at(t + 1);
        const c = at(t + 2);
        sampleTriangle(profile, [positions[a], positions[a + 1], positions[a + 2]], [positions[b], positions[b + 1], positions[b + 2]], [positions[c], positions[c + 1], positions[c + 2]], budget, false);
    }
}
function sampleTriangle(profile, a, b, c, budget, clipped) {
    const [ax, ay, az] = a;
    const [bx, by, bz] = b;
    const [cx, cy, cz] = c;
    const far = SIGHT.rings[SIGHT.rings.length - 1];
    if (nearest(ax, az, bx, bz, cx, cz) >= far)
        return;
    const edge = Math.max(Math.hypot(bx - ax, by - ay, bz - az), Math.hypot(cx - ax, cy - ay, cz - az), Math.hypot(cx - bx, cy - by, cz - bz));
    if (edge > WHOLE_EDGE && !clipped) {
        // A huge triangle costs what the part in range costs: cut to the square around the last ring
        // (edges of at most 85 m), then sampled as a fan of triangles.
        let polygon = [a, b, c];
        for (const [axis, limit, sign] of [
            [0, far, 1],
            [0, -far, -1],
            [2, far, 1],
            [2, -far, -1],
        ]) {
            polygon = clipSide(polygon, axis, limit, sign);
        }
        for (let k = 1; k + 1 < polygon.length; k++) {
            sampleTriangle(profile, polygon[0], polygon[k], polygon[k + 1], budget, true);
        }
        return;
    }
    const n = Math.min(200, Math.max(1, Math.ceil(edge / (SIGHT.step * budget.scale))));
    budget.left -= ((n + 1) * (n + 2)) / 2;
    if (budget.left < 0) {
        // Over budget: the points go wider (and a quarter of the budget is allowed again at that width).
        budget.scale *= 2;
        budget.left = SIGHT_BUDGET / 4;
    }
    for (let i = 0; i <= n; i++) {
        for (let j = 0; i + j <= n; j++) {
            const u = i / n;
            const v = j / n;
            addSightPoint(profile, ax + (bx - ax) * u + (cx - ax) * v, ay + (by - ay) * u + (cy - ay) * v, az + (bz - az) * u + (cz - az) * v);
        }
    }
}
/** A box (a prop's, axis-aligned in world space) as its top face, points at most `SIGHT.step` apart. */
export function addBoxSight(profile, box) {
    const nx = Math.max(1, Math.ceil((box.max[0] - box.min[0]) / SIGHT.step));
    const nz = Math.max(1, Math.ceil((box.max[2] - box.min[2]) / SIGHT.step));
    for (let i = 0; i <= nx; i++) {
        for (let j = 0; j <= nz; j++) {
            addSightPoint(profile, box.min[0] + ((box.max[0] - box.min[0]) * i) / nx, box.max[1], box.min[2] + ((box.max[2] - box.min[2]) * j) / nz);
        }
    }
}
/** Two profiles as one: the higher elevation in every cell. */
export function mergeSight(a, b) {
    return a.map((row, i) => row.map((value, k) => Math.max(value, b[i]?.[k] ?? SIGHT.none)));
}
/**
 * A profile as text, for data written into code (a code world's, ADR-053): rows by `;`, cells by `,`,
 * an empty cell for `SIGHT.none`.
 */
export function encodeSight(profile) {
    return profile
        .map((row) => row.map((value) => (value <= SIGHT.none ? '' : String(value))).join(','))
        .join(';');
}
/** `encodeSight`'s text as a profile; throws on text of another shape. */
export function decodeSight(text) {
    const rows = text.split(';');
    if (rows.length !== SIGHT.bins)
        throw new Error(`a sight profile has ${SIGHT.bins} rows`);
    return rows.map((row) => {
        const cells = row.split(',');
        if (cells.length !== SIGHT.rings.length) {
            throw new Error(`a sight profile row has ${SIGHT.rings.length} cells`);
        }
        return cells.map((cell) => {
            if (cell === '')
                return SIGHT.none;
            const value = Number(cell);
            if (!Number.isInteger(value))
                throw new Error(`"${cell}" is not a whole number of degrees`);
            return value;
        });
    });
}
/** The ground plan point at the centre of a profile cell. */
function sightCellCentre(bin, ring) {
    const rings = SIGHT.rings;
    const r = ring + 1 < rings.length
        ? (rings[ring] + rings[ring + 1]) / 2
        : rings[ring] + (rings[ring] - rings[ring - 1]) / 2;
    const azimuth = ((-180 + SIGHT.bin * (bin + 0.5)) * Math.PI) / 180;
    return [r * Math.sin(azimuth), -r * Math.cos(azimuth)];
}
/** `height` sampled at every cell's centre, to a tenth of a metre. */
export function sightGround(height) {
    return Array.from({ length: SIGHT.bins }, (_, bin) => SIGHT.rings.map((_ring, k) => {
        const [x, z] = sightCellCentre(bin, k);
        return Math.round(height(x, z) * 10) / 10 + 0;
    }));
}
/** The ground under (x, z): its cell's; 0 nearer than the first ring (the floor and the clear cylinder are flat) or unknown. */
export function groundAtSight(ground, x, z) {
    if (ground === undefined)
        return 0;
    const r = Math.hypot(x, z);
    let ring = -1;
    for (let k = 0; k < SIGHT.rings.length; k++)
        if (SIGHT.rings[k] <= r)
            ring = k;
    return ring < 0 ? 0 : (ground[sightBin(x, z)]?.[ring] ?? 0);
}
/** A ground as text, for data written into code: rows by `;`, cells by `,`. */
export function encodeGround(ground) {
    return ground.map((row) => row.map((h) => String(h)).join(',')).join(';');
}
/** `encodeGround`'s text as a ground; throws on text of another shape. */
export function decodeGround(text) {
    const rows = text.split(';');
    if (rows.length !== SIGHT.bins)
        throw new Error(`a sight ground has ${SIGHT.bins} rows`);
    return rows.map((row) => {
        const cells = row.split(',');
        if (cells.length !== SIGHT.rings.length) {
            throw new Error(`a sight ground row has ${SIGHT.rings.length} cells`);
        }
        return cells.map((cell) => {
            const value = cell === '' ? NaN : Number(cell);
            if (!Number.isFinite(value))
                throw new Error(`"${cell}" is not a height`);
            return value;
        });
    });
}
// --- where scenery stands (closer worlds, ADR-061) ---------------------------
/**
 * The scenery's occupancy (ADR-061): a grid of 0.5 m cells over ±30 m round the origin -- the
 * farthest a spot or ring goes -- each with the lowest and the highest height of any scenery in it,
 * in 0.2 m units up from y = 0 (`empty`: nothing). Measured from every mesh but the ground and the
 * `leaves` the API names (a route's part, modelled at the origin, drives elsewhere; the water's
 * surface and shafts of light are no obstacle), conservatively: a triangle marks every cell its box
 * covers, a wide one first cut in four; what lies at or below 5 cm (floors, water, the sea bed) is not
 * scenery. Ground cover by name (`soundsLikeCover`: grass, flowers, ferns) goes to a layer of its own,
 * `cover`, so a library model may stand in it and a crown over it does not make the grass a wall.
 * Lesson objects keep out of it (scenery.ts in lesson-schema).
 */
export const SCENERY = {
    cell: 0.5,
    half: 30,
    side: 120,
    unit: 0.2,
    empty: 255,
    /** The highest value a cell holds: 50.8 m (higher is capped; no lesson object goes there). */
    top: 254,
    /** A triangle wider than this either way, metres, is cut in four before it marks cells. */
    piece: 1,
    depth: 12,
    /** Cuts per package before wide triangles mark their whole box: a coarser grid, never a stuck worker. */
    pieces: 4_000_000,
};
/** A fresh budget, one per package. */
export function sceneryBudget() {
    return { left: SCENERY.pieces };
}
function emptyLayer() {
    const n = SCENERY.side * SCENERY.side;
    return {
        low: new Uint8Array(n).fill(SCENERY.empty),
        high: new Uint8Array(n).fill(SCENERY.empty),
    };
}
/** A grid with nothing in it, and an empty cover layer, to add to. */
export function emptyScenery() {
    return { ...emptyLayer(), cover: emptyLayer() };
}
/** Words in a mesh's name that say foliage: a name part (its letters) starting or ending so. */
export const FOLIAGE_WORDS = [
    'kruin',
    'blad',
    'boom',
    'struik',
    'gras',
    'riet',
    'kelp',
    'plant',
    'bloem',
    'tak',
    'leaf',
    'leaves',
    'foliage',
    'tree',
    'bush',
    'shrub',
    'grass',
    'reed',
    'flower',
];
/** Words that say a mesh is not the foliage itself (a trunk, the ground, a bench's top, a reed roof, a fan's blade): a name part holding one. */
export const NOT_FOLIAGE_WORDS = [
    'stam',
    'schors',
    'grond',
    'bank',
    'tafel',
    'vloer',
    'muur',
    'dak',
    'veld',
    'trunk',
    'bark',
    'ground',
    'floor',
    'table',
    'bench',
    'roof',
    'ventilator',
    'propeller',
    'rotor',
    'wiek',
];
/** The foliage that is ground cover: grass, flowers, tufts, sea grass, ferns, clover. */
export const COVER_WORDS = [
    'gras',
    'grass',
    'bloem',
    'flower',
    'tuft',
    'varen',
    'fern',
    'klaver',
    'clover',
];
/** Words that say it stands taller than cover, or in something: a crown, a tree, a bush, a planter. */
const NOT_COVER_WORDS = [
    'kruin',
    'boom',
    'tree',
    'struik',
    'bush',
    'shrub',
    'tak',
    'bak',
    'pot',
    'vaas',
    'vase',
];
const nameParts = (name) => name
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter(Boolean);
/** A mesh name or picture name that says foliage, and nothing that says trunk, ground or furniture. */
export function soundsLikeFoliage(name) {
    const parts = nameParts(name);
    if (parts.some((part) => NOT_FOLIAGE_WORDS.some((word) => part.includes(word))))
        return false;
    return parts.some((part) => FOLIAGE_WORDS.some((word) => part.startsWith(word) || part.endsWith(word)));
}
/** A mesh name that says ground cover (`COVER_WORDS`), and nothing that says more than foliage or a crown. */
export function soundsLikeCover(name) {
    const parts = nameParts(name);
    const not = [...NOT_FOLIAGE_WORDS, ...NOT_COVER_WORDS];
    if (parts.some((part) => not.some((word) => part.includes(word))))
        return false;
    return parts.some((part) => COVER_WORDS.some((word) => part.startsWith(word) || part.endsWith(word)));
}
const sceneryColumn = (v) => Math.floor((v + SCENERY.half) / SCENERY.cell);
/**
 * Marks every cell under the ground-plan box [x0, x1] × [z0, z1] as holding scenery from y0 to y1, in
 * the scenery's layer or (`'cover'`) the cover's (nothing when the grid has none).
 */
export function markScenery(grid, x0, z0, x1, z1, y0, y1, layer = 'scenery') {
    const into = layer === 'cover' ? grid.cover : grid;
    if (into)
        markLayer(into, x0, z0, x1, z1, y0, y1);
}
function markLayer(grid, x0, z0, x1, z1, y0, y1) {
    if (y1 <= STAGE.apronHeight)
        return;
    const lo = Math.min(SCENERY.top, Math.floor(Math.max(0, y0) / SCENERY.unit + 1e-9));
    const hi = Math.min(SCENERY.top, Math.max(lo, Math.ceil(y1 / SCENERY.unit - 1e-9)));
    const i0 = Math.max(0, sceneryColumn(x0));
    const i1 = Math.min(SCENERY.side - 1, sceneryColumn(x1));
    const j0 = Math.max(0, sceneryColumn(z0));
    const j1 = Math.min(SCENERY.side - 1, sceneryColumn(z1));
    for (let j = j0; j <= j1; j++) {
        for (let i = i0; i <= i1; i++) {
            const k = j * SCENERY.side + i;
            if (grid.low[k] === SCENERY.empty || grid.low[k] > lo)
                grid.low[k] = lo;
            if (grid.high[k] === SCENERY.empty || grid.high[k] < hi)
                grid.high[k] = hi;
        }
    }
}
function sceneryPiece(grid, a, b, c, budget, depth) {
    const x0 = Math.min(a[0], b[0], c[0]);
    const x1 = Math.max(a[0], b[0], c[0]);
    const z0 = Math.min(a[2], b[2], c[2]);
    const z1 = Math.max(a[2], b[2], c[2]);
    const y1 = Math.max(a[1], b[1], c[1]);
    if (y1 <= STAGE.apronHeight)
        return;
    if (x1 < -SCENERY.half || x0 > SCENERY.half || z1 < -SCENERY.half || z0 > SCENERY.half)
        return;
    if (Math.max(x1 - x0, z1 - z0) > SCENERY.piece && depth < SCENERY.depth && budget.left >= 4) {
        budget.left -= 4;
        const mid = (p, q) => [
            (p[0] + q[0]) / 2,
            (p[1] + q[1]) / 2,
            (p[2] + q[2]) / 2,
        ];
        const ab = mid(a, b);
        const bc = mid(b, c);
        const ca = mid(c, a);
        sceneryPiece(grid, a, ab, ca, budget, depth + 1);
        sceneryPiece(grid, ab, b, bc, budget, depth + 1);
        sceneryPiece(grid, ca, bc, c, budget, depth + 1);
        sceneryPiece(grid, ab, bc, ca, budget, depth + 1);
        return;
    }
    markLayer(grid, x0, z0, x1, z1, Math.min(a[1], b[1], c[1]), y1);
}
/**
 * One mesh's triangles in world space (x, y, z per vertex; `index` or none) into `grid`: nothing for
 * a ground mesh (`grond…`) or one whose name starts with one of `leaves` (a route's part, the water's
 * surface, light); ground cover by name into the cover layer when the grid has one.
 */
export function addMeshScenery(grid, name, positions, index, leaves, budget = sceneryBudget()) {
    if (name.startsWith('grond') || leaves.some((leaf) => name.startsWith(leaf)))
        return;
    const count = index ? index.length : Math.floor(positions.length / 3);
    const at = (k) => {
        const v = (index ? index[k] : k) * 3;
        return [positions[v], positions[v + 1], positions[v + 2]];
    };
    const into = grid.cover && soundsLikeCover(name) ? grid.cover : grid;
    for (let t = 0; t + 2 < count; t += 3)
        sceneryPiece(into, at(t), at(t + 1), at(t + 2), budget, 0);
}
function mergeLayer(a, b) {
    const out = emptyLayer();
    for (let k = 0; k < out.low.length; k++) {
        const lows = [a.low[k], b.low[k]].filter((v) => v !== SCENERY.empty);
        const highs = [a.high[k], b.high[k]].filter((v) => v !== SCENERY.empty);
        if (lows.length > 0)
            out.low[k] = Math.min(...lows);
        if (highs.length > 0)
            out.high[k] = Math.max(...highs);
    }
    return out;
}
/** Two grids as one: the lower low and the higher high in every cell, of each layer. */
export function mergeScenery(a, b) {
    const one = a.cover ?? b.cover;
    const cover = one === undefined ? undefined : mergeLayer(a.cover ?? one, b.cover ?? one);
    return { ...mergeLayer(a, b), ...(cover ? { cover } : {}) };
}
const BASE64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
/** The length of a grid as text, measured before ground cover by name (two layers of bytes). */
export const SCENERY_CHARS = Math.ceil((2 * SCENERY.side * SCENERY.side) / 3) * 4;
/** The length of a grid with its cover layer as text (four layers of bytes). */
export const SCENERY_COVER_CHARS = Math.ceil((4 * SCENERY.side * SCENERY.side) / 3) * 4;
/** Whether `text` has the length of a grid as text, either kind. */
export function isSceneryText(text) {
    return (typeof text === 'string' &&
        (text.length === SCENERY_CHARS || text.length === SCENERY_COVER_CHARS));
}
/**
 * A grid as text, for the package's measurement: base64 of its lows, then its highs, then -- when it
 * has its cover layer -- the cover's lows and highs.
 */
export function encodeScenery(grid) {
    const n = grid.low.length;
    const layers = grid.cover
        ? [grid.low, grid.high, grid.cover.low, grid.cover.high]
        : [grid.low, grid.high];
    const bytes = new Uint8Array(layers.length * n);
    layers.forEach((layer, i) => bytes.set(layer, i * n));
    let out = '';
    for (let i = 0; i < bytes.length; i += 3) {
        const v = (bytes[i] << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0);
        out += BASE64[(v >> 18) & 63] + BASE64[(v >> 12) & 63];
        out += i + 1 < bytes.length ? BASE64[(v >> 6) & 63] : '=';
        out += i + 2 < bytes.length ? BASE64[v & 63] : '=';
    }
    return out;
}
/** `encodeScenery`'s text as a grid (with its cover layer when the text has one); throws on text of another shape. */
export function decodeScenery(text) {
    if (!isSceneryText(text)) {
        throw new Error(`a scenery grid is ${SCENERY_CHARS} or ${SCENERY_COVER_CHARS} characters`);
    }
    const n = SCENERY.side * SCENERY.side;
    const layers = text.length === SCENERY_CHARS ? 2 : 4;
    const bytes = new Uint8Array(layers * n);
    let at = 0;
    for (let i = 0; i < text.length; i += 4) {
        let v = 0;
        for (let k = 0; k < 4; k++) {
            const ch = text[i + k];
            const digit = ch === '=' ? 0 : BASE64.indexOf(ch);
            if (digit < 0)
                throw new Error('a scenery grid is base64');
            v = (v << 6) | digit;
        }
        for (let k = 0; k < 3 && at < bytes.length; k++)
            bytes[at++] = (v >> (16 - 8 * k)) & 255;
    }
    const grid = { low: bytes.slice(0, n), high: bytes.slice(n, 2 * n) };
    return layers === 2
        ? grid
        : { ...grid, cover: { low: bytes.slice(2 * n, 3 * n), high: bytes.slice(3 * n) } };
}
// --- the visible ground against the height map (16 Oct 2026) ------------------
/**
 * Models stand on the height map (the headset's `ground`: 0 on the floor, `heightAt` beyond), never on
 * the meshes a pupil sees. Where the visible ground lies apart from the map a model floats or sinks;
 * this measures how far, on a grid over the map where models stand. The visible ground is the top of
 * the ground meshes (`grond…`, as the headset lays them); where there is none, the land a world made
 * of other meshes (`land`, `veld`, `plein`): of the surfaces there that face up, the one nearest the
 * map, below it at any depth or above it by at most `above` (not a roof over a hole or a ledge). What
 * hangs over the stage is never ground: a dome or sphere around it (a photo, a ceiling) not at all, a
 * roof over the stage in a land mesh only itself; nor is what lies more than `depth` below the floor
 * and the map (a world in space).
 */
export const GROUND_FIT = {
    /** The sample grid's spacing, metres. */
    step: 0.5,
    /**
     * Sampled within this of the middle, metres: the platform offers models spots up to 30 m out
     * (`MODEL_SPOTS.radiusMax`) and a run to a close-up starts 10 m further; beyond it a world may keep
     * its far land coarse.
     */
    radius: 40,
    /**
     * Further apart than this, metres, and a model visibly floats or sinks: more than the 2 cm a lesson
     * object may dip into the floor, the 5 cm of the clear space.
     */
    tolerance: 0.05,
    /**
     * Another mesh than a ground mesh counts as the ground at most this far above the map, metres:
     * higher it is a roof over a hole or a ledge, and the place has no visible ground (`bare`). Below
     * the map it counts at any depth: a land a map was set too high over is still the land.
     */
    above: 3,
    /**
     * A surface of another mesh than a ground mesh more than this many metres below the floor *and*
     * below the map is no ground there (20 Oct 2026, ADR-049 amended): a world in space has a platform
     * and nothing to stand on round it, and what lies far below (a disc 1648 m down, the inside of a
     * sky's floor) is no land a model stands on. The place is bare, a warning. A `grond…` mesh is always
     * the ground, at any depth; deep land the map follows (a tube, a canyon) is judged.
     */
    depth: 10,
    /**
     * And this many metres more per unit of the map's slope there: a big model's feet stand a metre
     * apart, so on a slope one foot already stands this far into or above the land, and a map of nodes
     * up to 1 m apart cannot follow a ditch's bank or a ridge between them.
     */
    slope: 0.5,
    /**
     * Refused only where the samples off lie together (side by side, not diagonally) over at least this
     * much land, m² (16 samples), with a 1 m × 1 m block of them in it: a big model's footprint.
     * Scattered tussocks and creases, and a thin line however long (a ditch), are no place a model stands.
     */
    area: 4,
    /** How many places a measurement names, at least `apart` metres from each other. */
    places: 3,
    apart: 3,
    /** The triangles are kept in square buckets of this side, metres. */
    cell: 2,
    /** At most this many triangle-in-bucket entries; past it the ground is not measured. */
    entries: 4_000_000,
};
/** An empty surface keeping what lies within `reach` metres of the middle each way (the sampled disc by default). */
export const emptyGroundSurface = (reach = GROUND_FIT.radius) => ({
    corners: [],
    ground: [],
    buckets: new Map(),
    reach: Math.ceil(Math.min(reach, 2100) / GROUND_FIT.cell) + 1,
    entries: 0,
    overflow: false,
});
/** The corners of triangle `t` (x, y, z each). */
function cornersOf(positions, index, t) {
    const out = [];
    for (let k = 0; k < 3; k++) {
        const v = (index ? index[t + k] : t + k) * 3;
        out.push(positions[v], positions[v + 1], positions[v + 2]);
    }
    return out;
}
/** Whether a triangle lies wholly above the clear cylinder's height and reaches over the floor. */
function overStage(p) {
    return (Math.min(p[1], p[4], p[7]) > STAGE.clearHeight &&
        nearest(p[0], p[2], p[3], p[5], p[6], p[8]) <= STAGE.floor);
}
/** The y of a triangle's normal, unnormalised: above 0 when it faces up. */
function upOf(p) {
    return (p[5] - p[2]) * (p[6] - p[0]) - (p[3] - p[0]) * (p[8] - p[2]);
}
/**
 * Whether a mesh is a dome or sphere round the stage (a photo, a ceiling): a triangle over the floor,
 * above the clear cylinder, facing down at it -- its inside, seen from the stage.
 */
function domeOver(positions, index, count) {
    for (let t = 0; t + 2 < count; t += 3) {
        const p = cornersOf(positions, index, t);
        if (overStage(p) && upOf(p) < 0)
            return true;
    }
    return false;
}
const bucketOf = (surface, i, j) => (i + surface.reach) * (2 * surface.reach + 1) + (j + surface.reach);
/**
 * One mesh's triangles in world space into `surface`: only those that face up (seen from above: the
 * inside of a dome faces down, a wall sideways) and reach the sampled disc. Left out: a mesh whose
 * name starts with one of `leaves` (a route's part, the water's surface, light: as the scenery grid
 * leaves them); one, not ground, that is a dome or sphere round the stage (`domeOver`: a photo, a
 * ceiling); and of another, not ground, the triangles over the stage above the clear cylinder (a roof
 * or canopy in the same mesh as the land).
 */
export function addGroundMesh(surface, name, positions, index, leaves = []) {
    if (surface.overflow || leaves.some((leaf) => name.startsWith(leaf)))
        return;
    const ground = name.startsWith('grond') ? 1 : 0;
    const count = index ? index.length : Math.floor(positions.length / 3);
    if (!ground && domeOver(positions, index, count))
        return;
    const cell = GROUND_FIT.cell;
    const lo = (a) => Math.max(-surface.reach, Math.floor(a / cell));
    const hi = (a) => Math.min(surface.reach - 1, Math.floor(a / cell));
    for (let t = 0; t + 2 < count; t += 3) {
        const a = (index ? index[t] : t) * 3;
        const b = (index ? index[t + 1] : t + 1) * 3;
        const c = (index ? index[t + 2] : t + 2) * 3;
        const ax = positions[a];
        const ay = positions[a + 1];
        const az = positions[a + 2];
        const bx = positions[b];
        const by = positions[b + 1];
        const bz = positions[b + 2];
        const cx = positions[c];
        const cy = positions[c + 1];
        const cz = positions[c + 2];
        if (![ax, ay, az, bx, by, bz, cx, cy, cz].every(Number.isFinite))
            continue;
        if (!ground && overStage([ax, ay, az, bx, by, bz, cx, cy, cz]))
            continue;
        // The normal's y against its length: up when the corners turn anticlockwise seen from above.
        const ux = bx - ax;
        const uy = by - ay;
        const uz = bz - az;
        const wx = cx - ax;
        const wy = cy - ay;
        const wz = cz - az;
        const ny = uz * wx - ux * wz;
        if (ny <= 0.05 * Math.hypot(uy * wz - uz * wy, ny, ux * wy - uy * wx))
            continue;
        const i0 = lo(Math.min(ax, bx, cx));
        const i1 = hi(Math.max(ax, bx, cx));
        const j0 = lo(Math.min(az, bz, cz));
        const j1 = hi(Math.max(az, bz, cz));
        if (i1 < i0 || j1 < j0)
            continue;
        surface.entries += (i1 - i0 + 1) * (j1 - j0 + 1);
        if (surface.entries > GROUND_FIT.entries) {
            surface.overflow = true;
            return;
        }
        const id = surface.ground.length;
        surface.corners.push(ax, ay, az, bx, by, bz, cx, cy, cz);
        surface.ground.push(ground);
        for (let i = i0; i <= i1; i++) {
            for (let j = j0; j <= j1; j++) {
                const key = bucketOf(surface, i, j);
                const list = surface.buckets.get(key);
                if (list)
                    list.push(id);
                else
                    surface.buckets.set(key, [id]);
            }
        }
    }
}
/**
 * The visible ground at (x, z): the top of the ground meshes there; where there is none, of the
 * other surfaces facing up the one nearest `near` (the map), below it at any depth or at most
 * `GROUND_FIT.above` above it (`near` undefined: the ground meshes only). Undefined where there is none.
 */
export function groundTop(surface, x, z, near) {
    const cell = GROUND_FIT.cell;
    const i = Math.floor(x / cell);
    const j = Math.floor(z / cell);
    const reach = surface.reach;
    if (i < -reach || i >= reach || j < -reach || j >= reach)
        return undefined;
    const list = surface.buckets.get(bucketOf(surface, i, j));
    if (!list)
        return undefined;
    const p = surface.corners;
    let top;
    let other;
    for (const id of list) {
        const o = id * 9;
        const ax = p[o];
        const az = p[o + 2];
        const bx = p[o + 3];
        const bz = p[o + 5];
        const cx = p[o + 6];
        const cz = p[o + 8];
        const d = (bx - ax) * (cz - az) - (cx - ax) * (bz - az);
        if (Math.abs(d) < 1e-12)
            continue;
        const s = ((x - ax) * (cz - az) - (cx - ax) * (z - az)) / d;
        const t = ((bx - ax) * (z - az) - (x - ax) * (bz - az)) / d;
        if (s < -1e-9 || t < -1e-9 || s + t > 1 + 1e-9)
            continue;
        const y = p[o + 1] + s * (p[o + 4] - p[o + 1]) + t * (p[o + 7] - p[o + 1]);
        if (surface.ground[id] === 1) {
            if (top === undefined || y > top)
                top = y;
        }
        else if (near !== undefined &&
            y - near <= GROUND_FIT.above + 1e-6 &&
            (other === undefined || Math.abs(y - near) < Math.abs(other - near)))
            other = y;
    }
    return top ?? other;
}
/**
 * Up to `GROUND_FIT.places` of `order`'s places, in its order, each at least `apart` from those before
 * and, given `accept`, accepted by it (asked only of a place far enough from those taken).
 */
function spread(xs, zs, order, accept) {
    const out = [];
    for (const k of order) {
        if (out.length >= GROUND_FIT.places)
            break;
        if (out.every((o) => Math.hypot(xs[o] - xs[k], zs[o] - zs[k]) >= GROUND_FIT.apart) &&
            (accept === undefined || accept(xs[k], zs[k])))
            out.push(k);
    }
    return out;
}
const centimetres = (v) => Math.round(v * 100) / 100;
/**
 * The visible ground against the map: every `step` over the map's area (`x`, `z` as its spec has them)
 * within `radius` and outside the floor, the ground there (`groundTop`) against `land` (the map, as the
 * headset reads it). `open` (the kit's) says whether a place lies in the open from the middle: the
 * places without ground it names (`bareAt`) are then only open ones. Nothing measured, `tooMany`, when
 * the meshes had too many triangles to collect.
 */
export function measureGroundFit(surface, area, land, open) {
    if (surface.overflow) {
        return { samples: 0, bare: 0, bareAt: [], off: 0, patch: 0, worst: [], tooMany: true };
    }
    const { step, radius } = GROUND_FIT;
    const xs = [];
    const zs = [];
    const bys = [];
    const bare = [];
    const off = [];
    /** The off samples by their place on the sample grid, for the patches. */
    const offAt = new Map();
    const key = (r, c) => (r + 4096) * 8192 + (c + 4096);
    let samples = 0;
    const first = (lo) => Math.ceil(Math.max(lo, -radius) / step - 1e-9);
    const last = (hi) => Math.floor(Math.min(hi, radius) / step + 1e-9);
    for (let r = first(area.z[0]); r <= last(area.z[1]); r++) {
        const z = r * step;
        for (let c = first(area.x[0]); c <= last(area.x[1]); c++) {
            const x = c * step;
            const from = Math.hypot(x, z);
            if (from <= STAGE.floor || from > radius)
                continue;
            samples++;
            const map = land(x, z);
            // A ground mesh's top is the ground at any depth; another surface far below the floor and the
            // map is no ground (a world in space), not a ground far off.
            const own = groundTop(surface, x, z);
            const found = own ?? groundTop(surface, x, z, map);
            const top = own === undefined &&
                found !== undefined &&
                found < -GROUND_FIT.depth &&
                map - found > GROUND_FIT.depth
                ? undefined
                : found;
            const h = step / 2;
            const slope = Math.hypot(land(x + h, z) - land(x - h, z), land(x, z + h) - land(x, z - h)) / step;
            if (top === undefined) {
                bare.push(xs.length);
                xs.push(x);
                zs.push(z);
                bys.push(0);
            }
            else if (Math.abs(top - map) > GROUND_FIT.tolerance + GROUND_FIT.slope * slope) {
                offAt.set(key(r, c), xs.length);
                off.push(xs.length);
                xs.push(x);
                zs.push(z);
                bys.push(top - map);
            }
        }
    }
    // The patches: off samples side by side, each found once.
    const enough = Math.round(GROUND_FIT.area / (step * step));
    const seen = new Set();
    const together = [];
    let patch = 0;
    for (const start of offAt.keys()) {
        if (seen.has(start))
            continue;
        seen.add(start);
        const members = [start];
        for (let m = 0; m < members.length; m++) {
            const k = members[m];
            for (const next of [k - 8192, k + 8192, k - 1, k + 1]) {
                if (!seen.has(next) && offAt.has(next)) {
                    seen.add(next);
                    members.push(next);
                }
            }
        }
        // A footprint fits: some sample with all eight round it off too (1 m × 1 m), not a thin line. The
        // places named are those middles, where a model would stand off the ground.
        const middles = members.filter((k) => [-8192, 0, 8192].every((dr) => [-1, 0, 1].every((dc) => offAt.has(k + dr + dc))));
        if (middles.length === 0)
            continue;
        patch = Math.max(patch, members.length);
        if (members.length >= enough)
            for (const k of middles)
                together.push(offAt.get(k));
    }
    const nearFirst = [...bare].sort((a, b) => Math.hypot(xs[a], zs[a]) - Math.hypot(xs[b], zs[b]));
    const furthestFirst = [...(together.length > 0 ? together : off)].sort((a, b) => Math.abs(bys[b]) - Math.abs(bys[a]));
    return {
        samples,
        bare: bare.length,
        bareAt: spread(xs, zs, nearFirst, open).map((k) => [centimetres(xs[k]), centimetres(zs[k])]),
        off: off.length,
        patch,
        worst: spread(xs, zs, furthestFirst).map((k) => ({
            at: [centimetres(xs[k]), centimetres(zs[k])],
            by: centimetres(bys[k]),
        })),
        depth: GROUND_FIT.depth,
        ...(sunkGround(surface) ? { sunk: true } : {}),
    };
}
/** Whether a ground mesh's triangle lies wholly more than `GROUND_FIT.depth` below the floor. */
function sunkGround(surface) {
    const p = surface.corners;
    for (let id = 0; id < surface.ground.length; id++) {
        if (surface.ground[id] !== 1)
            continue;
        const o = id * 9;
        if (Math.max(p[o + 1], p[o + 4], p[o + 7]) < -GROUND_FIT.depth)
            return true;
    }
    return false;
}
//# sourceMappingURL=measure.js.map